import { Icon, Persona, PersonaSize, TooltipHost } from "@fluentui/react";
import { Flex } from "components/styled";
import React from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { formatDateString, formatDateStringTimeOnly } from "utils/utils";

interface VehicleHistoryItem {
  setAt: string;
  setByUserName: string;
  setByUserEmail: string;
  action: string;
  info?: string;
  urgent: boolean;
  isInternalUser: boolean;
}

interface VehicleHistoryTableProps {
  data: VehicleHistoryItem[];
  // only LT reports urgency (SHOW_URGENT_ON_STATUS_CHANGE)
  showUrgent?: boolean;
}

export const VehicleHistoryTable: React.FC<VehicleHistoryTableProps> = ({
  data,
  showUrgent = true,
}) => {
  const { t } = useTranslation();
  const theme = useTheme() as IStyledTheme;

  return (
    <Table>
      <thead>
        <tr>
          <th>{t("bfm.user")}</th>
          <th>{t("bfm.fleet.history.header.action")}</th>
          <th>{t("bfm.details.label")}</th>
          {showUrgent && <th>{t("bfm.urgent.label")}</th>}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {data?.map((item, index) => (
          <tr
            key={`${item.setAt}-${index}`}
            style={{ borderBottom: "1px solid rgb(237, 235, 233)" }}
          >
            <td>
              <Flex justify="center">
                <Persona
                  text={item.setByUserName || "System"}
                  title={`${item.setByUserName || "System"} ${
                    item.setByUserEmail ? `(${item.setByUserEmail})` : ""
                  }`}
                  hidePersonaDetails
                  size={PersonaSize.size32}
                />
              </Flex>
            </td>
            <td>
              <FleetActionCell justify="flex-start">
                {item.action}
              </FleetActionCell>
            </td>

            <td>
              <Cell justify="flex-start">
                {item.info ? (
                  <div dangerouslySetInnerHTML={{ __html: item.info }} />
                ) : (
                  "-"
                )}
              </Cell>
            </td>
            {showUrgent && (
              <td>
                <Cell justify="flex-start">
                  {item.urgent ? t("greco.yes") : t("greco.no")}
                </Cell>
              </td>
            )}
            <td>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      fontStyle: "italic",
                      color: theme.palette.neutralSecondaryAlt,
                    }}
                  >
                    {formatDateString(item.setAt)}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      fontStyle: "italic",
                      color: theme.palette.neutralSecondaryAlt,
                    }}
                  >
                    {formatDateStringTimeOnly(item.setAt)}
                  </div>
                </div>
                {!item.setByUserName && !item.setByUserEmail ? (
                  <TooltipHost content={t("bfm.system.label")}>
                    <IconStyled iconName="System" />
                  </TooltipHost>
                ) : item.isInternalUser ? (
                  <TooltipHost content={t("bfm.grecoUser.label")}>
                    <IconStyled iconName="BusinessCenterLogo" />
                  </TooltipHost>
                ) : (
                  <TooltipHost content={t("bfm.externalUser.label")}>
                    <IconStyled iconName="Contact" />
                  </TooltipHost>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export const Table = styled.table`
  border-collapse: collapse;
  border-top: 1px solid ${(p) => p.theme.palette.neutralLighterAlt};
  width: 100%;
  td {
    padding: 5px 8px;
  }
  th {
    padding: 5px;
    font-size: 12px;
    font-weight: 650;
    border-bottom: 1px solid ${(p) => p.theme.palette.neutralLighterAlt};
    height: 42px;
    color: ${(p) => p.theme.palette.neutralPrimary};
  }
`;

const FleetActionCell = styled(Flex)`
  font-size: 12px;
  font-style: italic;
  color: ${(p) => p.theme.palette.neutralPrimary};
  text-align: center;
`;

const PeriodCell = styled(Flex)`
  font-size: 12px;
  color: ${(p) => p.theme.palette.themePrimary};
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  font-style: italic;
  text-align: flex-start;
`;

const Cell = styled(Flex)`
  font-size: 12px;
  color: ${(p) => p.theme.palette.neutralSecondary};
  text-align: flex-start;
`;

const IconStyled = styled(Icon)`
  color: ${(p) => (p.theme.isDark ? "white" : "black")};
`;
